import React from 'react';
import { Card, CardContent, Typography, Grid, Box, useTheme, useMediaQuery } from '@mui/material';
import BigNumber from 'bignumber.js';

import { LogStats } from 'types';
import { convertWeiToTokens } from 'utilities';
import { TOKENS } from 'constants/tokens';

interface LogStatsCardProps {
  stats: LogStats;
  loading?: boolean;
}

const formatAmount = (amount: string) => {
  try {
    return convertWeiToTokens({
      valueWei: new BigNumber(amount),
      token: TOKENS.dl,
      returnInReadableFormat: true,
    });
  } catch {
    return amount;
  }
};

const StatItem: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Box>
    <Typography variant="h6" color="primary">
      {value}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
  </Box>
);

const LogStatsCard: React.FC<LogStatsCardProps> = ({ stats, loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            加载统计数据中...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
          合约统计
        </Typography>
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="总质押次数" value={stats.totalStakes} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="总提现次数" value={stats.totalWithdraws} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="总奖励次数" value={stats.totalRewards} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="活跃用户" value={stats.activeUsers} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="总质押金额" value={formatAmount(stats.totalStakeAmount)} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="总提现金额" value={formatAmount(stats.totalWithdrawAmount)} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="总奖励金额" value={formatAmount(stats.totalRewardAmount)} />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatItem label="网体奖励金额" value={formatAmount(stats.totalNetworkRewardAmount)} />
          </Grid>
        </Grid>
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            最后更新: {new Date(stats.lastUpdated * 1000).toLocaleString()}
          </Typography>
          <Typography variant="caption" display="block" color="textSecondary" mt={1}>
            数据来源: Log API (localhost:3001)
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LogStatsCard; 